// src/encounter/lib/vaccine-catalogue-defaults.ts
// The vaccine dialog's by-code catalogue merge (PROD-1819), as pure functions so it can be derived
// during render (PROD-2910) and unit-tested on its own. Kept out of vaccine-add-dialog.tsx so that
// file exports components only (react-refresh/only-export-components).
import type { VaccineValueSet } from '@/shared/services/use-vaccination-search';

/** True when a value-set is missing the fields only the DDC catalogue carries. */
export function needsCatalogueLookup(v: VaccineValueSet | null): boolean {
  if (!v) return false;
  return !v.unitOfMeasurement || (v.route?.length ?? 0) === 0;
}

/**
 * The picked vaccine with the by-code catalogue row's route/uom/system adopted (PROD-1819), derived
 * during render rather than written back into state from an effect (PROD-2910). Whatever the pick
 * already carries wins: a saved edit keeps its own route and unit, and only the gaps are filled.
 * Match on CODE, never on position: the search is a text match, so the first row is not necessarily
 * the vaccine that was picked (and `placeholderData` can briefly hand back a previous code's rows).
 */
export function withCatalogueDefaults(picked: VaccineValueSet | null, catalogue: readonly VaccineValueSet[] | undefined): VaccineValueSet | null {
  if (!picked || !needsCatalogueLookup(picked)) return picked;
  const code = picked.valueCoding.code?.trim() ?? '';
  if (!code) return picked;
  const hit = (catalogue ?? []).find((r) => r.valueCoding.code?.trim() === code);
  if (!hit) return picked;
  // Adopt route/uom AND the catalogue's system — the authoritative coding for this product.
  return {
    ...picked,
    valueCoding: { ...picked.valueCoding, system: hit.valueCoding.system || picked.valueCoding.system },
    unitOfMeasurement: picked.unitOfMeasurement || hit.unitOfMeasurement,
    route: (picked.route?.length ?? 0) > 0 ? picked.route : hit.route,
  };
}
